import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const DISMISSED_KEY = "em_phone_banner_dismissed_v1";

/**
 * Soft prompt for patients who haven't added a phone number. Without a phone,
 * provider replies via Spruce/SMS can't reach them — the inbound webhook fails
 * to match them to their patient record. Dismissible (we don't nag).
 */
export default function PhonePromptBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && !!localStorage.getItem(DISMISSED_KEY)
  );

  if (!user || user.role === "admin") return null;
  if ((user.phone || "").trim()) return null;
  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-start gap-3"
      data-testid="phone-prompt-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
        <Phone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Add your phone number</p>
        <p className="text-xs text-muted-foreground leading-snug mt-0.5">
          We use this so your provider can reach you by SMS for replies, refill updates, and reminders. It only takes a moment.
        </p>
        <Link
          to="/profile"
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-2 hover:underline"
          data-testid="phone-prompt-banner-add"
        >
          Add it on your profile →
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground p-1"
        data-testid="phone-prompt-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
